import { observer } from 'mobx-react-lite';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { SettingsSection } from '@/features/settings/settings-section';
import { userStore } from '@/stores/user.store';

import { useTeamUsagePrefetch } from '../team.query';
import { teamStore } from '../team.store';
import {
  TeamTabTitle,
  type TeamDashboardData,
  type TeamTab,
} from '../team.types';

import { AllowedDomainsCard } from './AllowedDomainsCard';
import { MembersTable } from './MembersTable';
import { TeamBaaCard } from './TeamBaaCard';
import { TeamBillingCard } from './TeamBillingCard';
import { TeamChatModesCard } from './TeamChatModesCard';
import { TeamCustomInstructionsCard } from './TeamCustomInstructionsCard';
import { TeamEnforcedExclusionsCard } from './TeamEnforcedExclusionsCard';
import { TeamInclusionCard } from './TeamInclusionCard';
import { TeamIntegrationsCard } from './TeamIntegrationsCard';
import { TeamMeetingRulesCard } from './TeamMeetingRulesCard';
import { TeamSsoCard } from './TeamSsoCard';
import { TeamTranscriptStorageCard } from './TeamTranscriptStorageCard';
import { TeamUsageView } from './usage';

export const TeamDashboardView = observer(function TeamDashboardView({
  dashboard,
  tab,
  onTabChange,
}: {
  dashboard: TeamDashboardData;
  tab: TeamTab;
  onTabChange: (tab: TeamTab) => void;
}) {
  const { team, members, billing } = dashboard;
  const isAdmin = teamStore.isAdmin;
  useTeamUsagePrefetch(team.id);

  const hasActiveSubscription =
    billing.stripe_subscription_status != null &&
    ['active', 'trialing', 'past_due'].includes(
      billing.stripe_subscription_status
    );
  const showsInactiveNotice = !hasActiveSubscription && tab !== 'billing';

  return (
    <SettingsSection.Root>
      <SettingsSection.Header>
        <SettingsSection.Title>{TeamTabTitle[tab]}</SettingsSection.Title>
      </SettingsSection.Header>

      {showsInactiveNotice && (
        <XStack className="items-center gap-2 rounded-lg border border-warning-5 dark:border-warning-3 bg-warning-2 px-3 py-2">
          <Icons.warnTriangle className="w-3.5 h-3.5 text-warning-11 shrink-0" />
          <span className="flex-1 text-sm text-warning-11">
            {isAdmin
              ? 'Your team subscription is inactive.'
              : 'Your team subscription is inactive. Contact your team admin to restore access.'}
          </span>
          {isAdmin && (
            <Button
              size="xs"
              variant="outline"
              onClick={() => onTabChange('billing')}
            >
              Go to Billing
            </Button>
          )}
        </XStack>
      )}

      {tab === 'members' && (
        <YStack className="gap-8">
          <MembersTable
            team={team}
            members={members}
            isAdmin={isAdmin}
            currentUserEmail={userStore.user?.email}
            hasActiveSubscription={hasActiveSubscription}
          />
          {isAdmin && (
            <>
              <AllowedDomainsCard
                teamId={team.id}
                domains={team.allowed_domains ?? []}
              />
              <TeamSsoCard teamId={team.id} teamName={team.name} />
              <TeamBaaCard teamId={team.id} baaEnabled={team.baa_enabled} />
            </>
          )}
        </YStack>
      )}

      {tab === 'analytics' && (
        <TeamUsageView
          teamId={team.id}
          onInviteMember={() => onTabChange('members')}
        />
      )}

      {tab === 'data-controls' && (
        <YStack className="gap-8">
          <TeamEnforcedExclusionsCard
            teamId={team.id}
            apps={team.enforced_excluded_apps}
            domains={team.enforced_excluded_domains}
          />
          <TeamInclusionCard
            teamId={team.id}
            mode={team.enforced_app_access_mode}
            apps={team.enforced_allowed_apps}
          />
          <TeamTranscriptStorageCard
            teamId={team.id}
            storeTranscripts={team.store_meeting_transcripts}
          />
          <TeamMeetingRulesCard
            teamId={team.id}
            rules={team.meeting_recording_rules}
            teamDomains={team.allowed_domains}
            members={members}
            currentUserEmail={userStore.user?.email}
          />
          <TeamChatModesCard
            teamId={team.id}
            allowedModes={team.allowed_chat_modes}
          />
          <TeamIntegrationsCard
            teamId={team.id}
            allowedIds={team.allowed_integrations}
            options={team.integration_options}
          />
          <TeamCustomInstructionsCard
            teamId={team.id}
            instructions={team.custom_instructions}
          />
        </YStack>
      )}

      {tab === 'billing' && (
        <TeamBillingCard team={team} billing={billing} isAdmin={isAdmin} />
      )}
    </SettingsSection.Root>
  );
});
